import { forwardRef } from "react";
import { VideoMeetingTile } from "../VideoMeetingTile/VideoMeetingTile";
import { useContentShareZoom } from "../../hooks/useContentShareZoom";
import { Button } from "@themes/pollinator/ui/Button";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

type Props = {
  isContentSharing?: boolean;
};

export const VideoMeetingContentShare = forwardRef<HTMLVideoElement, Props>(
  ({ isContentSharing = true }, ref) => {
    const {
      containerRef,
      contentShareZoom,
      resetZoom,
      getMinScale,
      zoomToScale,
    } = useContentShareZoom();
    const { scale, translateX, translateY } = contentShareZoom;
    const minScale = getMinScale();
    const isAtMinZoom = scale <= minScale + 0.0001;
    const isAtMaxZoom = scale >= 3;

    const handleZoomIn = () => zoomToScale(scale * 1.2);
    const handleZoomOut = () => zoomToScale(scale * 0.8);

    return (
      <div
        ref={containerRef}
        className="w-full h-full flex flex-col items-center justify-center overflow-hidden relative"
        style={{
          touchAction: "none",
          cursor: contentShareZoom.isZoomed ? "grab" : "default",
        }}
      >
        <div className="w-full max-w-6xl h-full flex items-center justify-center">
          <div className="aspect-video relative overflow-hidden w-full h-full flex items-center justify-center rounded-md">
            <div
              className="w-full h-full flex items-center justify-center"
              style={{
                transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
                transformOrigin: "center center",
                transition: "transform 0.2s",
              }}
            >
              <VideoMeetingTile
                objectFit="contain"
                className="bg-brand-900 w-full h-full"
                isContentShare
                videoClassName="scale-[1.01]"
                ref={ref}
              />
            </div>
            {isContentSharing && (
              <div className="absolute top-4 left-4 flex flex-col gap-2 bg-brand-900/80 backdrop-blur rounded-lg p-2 z-10">
                <Button
                  onClick={handleZoomIn}
                  disabled={isAtMaxZoom}
                  variant="ghost"
                  size="sm"
                  iconComponent={ZoomIn}
                  title="Zoom In (Ctrl/Cmd + Scroll)"
                  theme="dark"
                />
                <Button
                  onClick={handleZoomOut}
                  disabled={isAtMinZoom}
                  variant="ghost"
                  size="sm"
                  iconComponent={ZoomOut}
                  title="Zoom Out (Ctrl/Cmd + Scroll)"
                  theme="dark"
                />
                <Button
                  onClick={resetZoom}
                  disabled={!contentShareZoom.isZoomed}
                  variant="ghost"
                  size="sm"
                  iconComponent={RotateCcw}
                  title="Reset Zoom"
                  theme="dark"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);
